import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import { Button, Checkbox, Divider, Grid, Link, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { sort } from 'fast-sort';
import {
  EstateInputData_CreateEstateInputDataInput,
  EstateInputData_FieldsFragment,
  EstateTask_CreateEstateTaskInput,
  EstateTaskDeadlineType,
  EstateTaskGroup,
  EstateTaskIdentifier,
  EstateType,
  FeatureSubscriptionType,
  useConditionalTemplateQuestionSet_GetAllConditionalTemplateQuestionSetsQuery,
  useDocumentGenerationTemplate_GetAllDocumentGenerationTemplateSelectListItemsQuery,
} from 'queries';
import * as yup from 'yup';

import AutocompleteFormSelectField from 'common/autocomplete-form-select-field';
import FormInputErrorMessage from 'common/form-input-error-message';
import FormSelectField from 'common/form-select-field';
import FormTextField from 'common/form-text-field';
import Spinner from 'common/spinner';
import { EstateTaskGroupLabel } from 'utils/estate';
import { getNameForFeatureSubscriptionType } from 'utils/feature-subscription';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import { EstateTaskIcon } from './estate-task-icon/estate-task-icon';
import RequiredInfoDocItem from './required-info-doc-item';

const useStyles = makeStyles(() => ({
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '15px',
  },
  h5: {
    paddingTop: '5px',
  },
  formSection: {
    paddingBottom: '10px',
  },
  form: {
    textAlign: 'left',
    '& input': {
      paddingRight: '10px',
      padding: '1vh 2vh',
    },
  },
  inputGrid: {
    paddingRight: '20px',
  },
  inputError: {
    backgroundColor: '#FFF1F1',
    color: 'red',
  },
  container: {
    paddingTop: '10px',
  },
  selectOption: {
    cursor: 'pointer',
  },
  reqItemsTitle: {
    marginTop: '20px',
  },
  addItem: {
    display: 'flex',
    margin: '10px auto',
    cursor: 'pointer',
  },
}));

const groupOptions = [
  // TRUADMIN
  EstateTaskGroup.TruadminFirmSpecificTasks,
  EstateTaskGroup.TruadminEstateOpening,
  EstateTaskGroup.TruadminAssetLiabilityDiscovery,
  EstateTaskGroup.TruadminAssetLiabilityManagement,
  EstateTaskGroup.TruadminPaymentsTaxes,
  EstateTaskGroup.TruadminDistributions,
  EstateTaskGroup.TruadminEstateClosure,

  // TRUPLANNING
  EstateTaskGroup.TruplanningFirmSpecificTasks,
  EstateTaskGroup.TruplanningEstatePlanning,
  EstateTaskGroup.TruplanningTrustFunding,
  EstateTaskGroup.TruplanningPostEstatePlanning,
  EstateTaskGroup.TruplanningAssetLiabilityDiscovery,
  EstateTaskGroup.TruplanningOpTipsForIrrevocableTrusts,
  EstateTaskGroup.TruplanningBusinessInterests,

  // TRUPROXY
  EstateTaskGroup.TruproxyFirmSpecificTasks,
  EstateTaskGroup.TruproxyGuardianshipsConservatorships,

  // TRUSTADMIN
  EstateTaskGroup.TrustadminFirmSpecificTasks,
  EstateTaskGroup.TrustadminGrantorAssetAndLiabilityDiscovery,
  EstateTaskGroup.TrustadminTrusteeDiligenceTrusteeAcceptance,
  EstateTaskGroup.TrustadminTrustAccountingTrustReporting,
  EstateTaskGroup.TrustadminPostDeathAdministration,
];
const categoryLabels: Record<string, string> = {
  Truadmin: 'Truadmin',
  Truplanning: 'Truplanning',
  Truproxy: 'Truproxy',
  Trustadmin: 'Trustadmin',
};

const groupOptionsWithLabels = groupOptions.map((group) => {
  const groupKey = Object.entries(EstateTaskGroup).find(([_, value]) => value === group)?.[0];
  const categoryKey = Object.keys(categoryLabels).find((key) => groupKey?.includes(key));

  return {
    label: EstateTaskGroupLabel[group] + ` (${categoryLabels[categoryKey!]})`,
    value: group,
  };
});

const schema = yup.object().shape({
  allowPaymentQuantity: yup.boolean().required('Required'),
  userCompletionRequired: yup.boolean().required('Required'),
  userCanAssignAgain: yup.boolean().required('Required'),
  autoDocumentGeneration: yup.boolean().required('Required'),
  deadlineType: yup.string().required('Required'),
  estateType: yup.string().required('Required'),
  group: yup.string().required('Required'),
  label: yup.string().required('Required'),
  identifier: yup.string().nullable(),
  sortOrder: yup.number().required('Required'),
  minimumFeatureSubscriptionType: yup.string().required('Required'),
  createEstateInputDatasInput: yup.array().of(
    yup.object({
      estateInputDataFormatId: yup.string().required('Required'),
    }),
  ),
  newEstateInputDatasInput: yup.array().of(
    yup.object({
      estateInputDataFormatId: yup.string().required('Required'),
    }),
  ),
});

type Props = {
  onSubmit: (formData: EstateTask_CreateEstateTaskInput) => void;
  onUpdateIsArchived?: (isArchived: boolean) => void;
  isSaveDisabled: boolean;
  values?: EstateTask_CreateEstateTaskInput & {
    isArchived: boolean;
    createEstateInputDatasInput: Partial<EstateInputData_FieldsFragment>[];
    iconUrl?: string | null;
  };
  isEdit?: boolean;
};

export type EstateTaskFormFormValues = EstateTask_CreateEstateTaskInput & {
  extraEstateInputDatasInput: EstateInputData_CreateEstateInputDataInput[];
};

const EstateTaskForm = ({
  onSubmit,
  onUpdateIsArchived,
  values,
  isEdit,
  isSaveDisabled,
}: Props) => {
  const classes = useStyles();
  const isArchived = Boolean(values?.isArchived);

  const {
    data: conditionalTemplateQuestionSetsData,
    loading: conditionalTemplateQuestionSetsDataLoading,
  } = useConditionalTemplateQuestionSet_GetAllConditionalTemplateQuestionSetsQuery({
    onError: (e) => {
      showAlert({
        message: 'Error getting conditional template question sets: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const { data: documentGenerationTemplatesData, loading: documentGenerationTemplatesDataLoading } =
    useDocumentGenerationTemplate_GetAllDocumentGenerationTemplateSelectListItemsQuery({
      onError: (e) => {
        showAlert({
          message: 'Error getting document generation templates: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
      fetchPolicy: 'no-cache',
      nextFetchPolicy: 'no-cache',
    });

  const formMethods = useForm<EstateTaskFormFormValues>({
    // @ts-expect-error - RHF V7 limitation #7895
    resolver: yupResolver(schema),
    defaultValues: {
      assignButtonLabel: '',
      deadlineMonthsFromDateOfDeath: 0,
      createEstateInputDatasInput: [],
      extraEstateInputDatasInput: [],
      hasAssignButton: false,
      labelHint: '',
      price: 0,
      isSearch: false,
      isFavorite: false,
      deadlineType: EstateTaskDeadlineType.None,
      allowPaymentQuantity: false,
      userCompletionRequired: false,
      userCanAssignAgain: false,
      minimumFeatureSubscriptionType: FeatureSubscriptionType.SmallFirm,
      autoDocumentGeneration: false,
      documentGenerationTemplateId: values?.documentGenerationTemplateId || '',
      conditionalTemplateQuestionSetId: values?.conditionalTemplateQuestionSetId || '',
      ...(values ? values : {}),
    },
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    watch,
    setValue,
  } = formMethods;

  const {
    fields: createEstateInputDatasInputFields,
    append: appendCreateEstateInputDataInput,
    remove: removeCreateEstateInputDataInput,
    update: updateCreateEstateInputDataInput,
  } = useFieldArray({
    control,
    name: 'createEstateInputDatasInput',
  });

  const {
    fields: extraEstateInputDatasFields,
    append: appendExtraEstateInputDataInput,
    remove: removeExtraEstateInputDataInput,
    update: updateExtraEstateInputDataInput,
  } = useFieldArray({
    control,
    name: 'extraEstateInputDatasInput',
  });

  const price = watch('price');
  const hasAssignButton = watch('hasAssignButton');
  const deadlineType = watch('deadlineType');
  const userCompletionRequired = watch('userCompletionRequired');
  const documentGenerationTemplateId = watch('documentGenerationTemplateId');
  const conditionalTemplateQuestionSetId = watch('conditionalTemplateQuestionSetId');

  const showAddNewEstateInputDatasButton =
    !documentGenerationTemplateId && !conditionalTemplateQuestionSetId;

  const onAddNewEstateInputDatasInput = () => {
    if (isEdit) {
      appendExtraEstateInputDataInput({
        estateInputDataFormatId: '',
      });
    } else {
      appendCreateEstateInputDataInput({
        estateInputDataFormatId: '',
      });
    }
  };

  const handleOnDocumentGenerationTemplateChange = (value: string | null) => {
    setValue('documentGenerationTemplateId', value ? value : null);
    setValue('conditionalTemplateQuestionSetId', null);
  };

  const handleOnconditionalTemplateQuestionSetChange = (value: string | null) => {
    setValue('conditionalTemplateQuestionSetId', value ? value : null);
    setValue('documentGenerationTemplateId', null);
  };

  const handleSubmitForm = (formData: EstateTaskFormFormValues) => {
    const { createEstateInputDatasInput, extraEstateInputDatasInput, ...others } = formData;

    onSubmit({
      ...others,
      createEstateInputDatasInput: [
        ...(createEstateInputDatasInput || []),
        ...extraEstateInputDatasInput,
      ],
    });
  };

  const handleUpdateIsArchived = () => {
    const archiveVerb = isArchived ? 'unarchive' : 'archive';
    if (!window.confirm(`Are you sure you want to ${archiveVerb} this estate task?`)) {
      return;
    }
    onUpdateIsArchived!(!isArchived);
  };

  if (conditionalTemplateQuestionSetsDataLoading || documentGenerationTemplatesDataLoading) {
    return <Spinner />;
  }

  const conditionalTemplateQuestionSetsSelectOptions = sort(
    conditionalTemplateQuestionSetsData?.data?.map(({ id, name }) => ({
      label: name,
      value: id,
    })) || [],
  ).asc((option) => option.label);

  const documentGenerationTemplateSelectOptions = sort(
    documentGenerationTemplatesData?.data?.map(({ id, label }) => ({
      label: label,
      value: id,
    })) || [],
  ).asc((option) => option.label);

  const getMinimumPlanOptions = () => {
    const excludedPlans = [
      FeatureSubscriptionType.None,
      FeatureSubscriptionType.Lite,
      FeatureSubscriptionType.SoloLegacy,
      FeatureSubscriptionType.Premium,
      FeatureSubscriptionType.Unlimited,
      FeatureSubscriptionType.Professional,
      FeatureSubscriptionType.ProfessionalPlus,
    ];
    return Object.values(FeatureSubscriptionType)
      .filter((type) => !excludedPlans.includes(type))
      .map((type) => ({
        label: getNameForFeatureSubscriptionType(type),
        value: type,
      }));
  };

  return (
    <FormProvider {...formMethods}>
      <Typography variant="h5" className={classes.h5}>
        Estate Task Details
      </Typography>
      <form
        id="estate-task-form"
        className={classes.form}
        onSubmit={handleSubmit(handleSubmitForm)}
      >
        <div className={classes.formSection}>
          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={6}>
              <Controller
                name="sortOrder"
                control={control}
                render={({ field: { ref, onChange, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    onChange={(evt) => {
                      const value = evt.target.value;
                      if (value && !isNaN(Number(value))) {
                        onChange(Number(value));
                      } else {
                        onChange(null);
                      }
                    }}
                    errorMessage={errors.sortOrder?.message}
                    label="Sort Order"
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="estateType"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormSelectField
                    {...field}
                    inputRef={ref}
                    options={[
                      { label: 'Truadmin', value: EstateType.Truadmin },
                      { label: 'Trustadmin', value: EstateType.Trustadmin },
                      { label: 'Truplanning', value: EstateType.Truplanning },
                      { label: 'Truproxy', value: EstateType.Truproxy },
                    ]}
                    label="Estate Type"
                    errorMessage={errors.estateType?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container className={classes.container}>
            <Grid item xs={12} sm={12}>
              <Controller
                name="group"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormSelectField
                    {...field}
                    inputRef={ref}
                    options={groupOptionsWithLabels}
                    label="Group"
                    errorMessage={errors.group?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container className={classes.container}>
            <Grid item xs={12} sm={12}>
              <Controller
                name="label"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    errorMessage={errors.label?.message}
                    label="Label"
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container className={classes.container}>
            <Grid item xs={12} sm={12}>
              <Controller
                name="labelHint"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    errorMessage={errors.labelHint?.message}
                    label="Label Hint"
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container className={classes.container}>
            <Grid
              item
              xs={2}
              sm={2}
              className={classes.inputGrid}
              style={{
                paddingTop: 10,
              }}
            >
              <Typography variant="caption">Is Search?</Typography>
              <Controller
                name="isSearch"
                control={control}
                render={({ field: { ref, onChange, value, ...field } }) => (
                  <Checkbox
                    {...field}
                    inputRef={ref}
                    color="primary"
                    onChange={(evt) => onChange(evt.target.checked)}
                    checked={Boolean(value)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={10} sm={10}>
              <Controller
                name="shortName"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    errorMessage={errors.labelHint?.message}
                    label="Short Name"
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container className={classes.container}>
            <Grid item xs={6} sm={6} className={classes.inputGrid}>
              <Controller
                name="price"
                control={control}
                render={({ field: { ref, onChange, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    onChange={(evt) => {
                      const value = evt.target.value;
                      if (value && !isNaN(Number(value))) {
                        onChange(Number(value));
                      } else {
                        onChange(null);
                      }
                    }}
                    errorMessage={errors.price?.message}
                    label="Price (CENTS)"
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={6}>
              <Controller
                name="dollarValue"
                control={control}
                render={({ field: { ref, onChange, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    onChange={(evt) => {
                      const value = evt.target.value;
                      if (value && !isNaN(Number(value))) {
                        onChange(Number(value));
                      } else {
                        onChange(null);
                      }
                    }}
                    errorMessage={errors.price?.message}
                    label="Dollar Value"
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container className={classes.container}></Grid>
          <Grid container className={classes.container}>
            <Grid
              item
              xs={12}
              sm={3}
              style={{
                paddingTop: 10,
              }}
            >
              <Typography variant="caption">Has Assign Button</Typography>
              <Controller
                name="hasAssignButton"
                control={control}
                render={({ field: { ref, onChange, value, ...field } }) => (
                  <Checkbox
                    {...field}
                    inputRef={ref}
                    color="primary"
                    onChange={(evt) => onChange(evt.target.checked)}
                    checked={Boolean(value)}
                    disabled={!price || userCompletionRequired}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={9}>
              <Controller
                name="assignButtonLabel"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    errorMessage={errors.assignButtonLabel?.message}
                    label="Assign Button Label"
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container className={classes.container}>
            <Grid item xs={12} sm={12}>
              <Controller
                name="identifier"
                control={control}
                render={({ field: { ref, onChange, ...field } }) => (
                  <FormSelectField
                    {...field}
                    inputRef={ref}
                    includeNullOption
                    options={[
                      { label: '', value: '' },
                      ...Object.entries(EstateTaskIdentifier).map(([key, identifier]) => ({
                        label: key,
                        value: identifier,
                      })),
                    ]}
                    onChange={(evt) => onChange(evt.target.value === '' ? null : evt.target.value)}
                    label="Identifier"
                    errorMessage={errors.identifier?.message}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container className={classes.container}>
            <Grid item xs={12} sm={6}>
              <Grid container>
                <Grid item xs={12} sm={12}>
                  <Typography variant="caption">
                    Allow Payment Qty{' '}
                    {errors.allowPaymentQuantity?.message && (
                      <FormInputErrorMessage error={errors.allowPaymentQuantity?.message} />
                    )}
                  </Typography>
                  <Controller
                    name="allowPaymentQuantity"
                    control={control}
                    render={({ field: { ref, onChange, value, ...field } }) => (
                      <Checkbox
                        {...field}
                        onChange={(evt) => onChange(evt.target.checked)}
                        inputRef={ref}
                        color="primary"
                        checked={value}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12} sm={12}>
                  <Typography variant="caption">User Completion Required</Typography>

                  <Controller
                    name="userCompletionRequired"
                    control={control}
                    render={({ field: { ref, onChange, value, ...field } }) => (
                      <Checkbox
                        {...field}
                        inputRef={ref}
                        onChange={(evt) => onChange(evt.target.checked)}
                        color="primary"
                        checked={value}
                        disabled={Boolean(hasAssignButton && !!price)}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12} sm={12}>
                  <Typography variant="caption">User Can Assign Again</Typography>

                  <Controller
                    name="userCanAssignAgain"
                    control={control}
                    render={({ field: { ref, onChange, value, ...field } }) => (
                      <Checkbox
                        {...field}
                        inputRef={ref}
                        onChange={(evt) => onChange(evt.target.checked)}
                        color="primary"
                        checked={value}
                        disabled={!hasAssignButton}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12} sm={12}>
                  <Typography variant="caption">Is Favorite?</Typography>

                  <Controller
                    name="isFavorite"
                    control={control}
                    render={({ field: { ref, onChange, value, ...field } }) => (
                      <Checkbox
                        {...field}
                        inputRef={ref}
                        onChange={(evt) => onChange(evt.target.checked)}
                        color="primary"
                        checked={value}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6}>
              <EstateTaskIcon
                defaultUrl={values?.iconUrl}
                onChange={(file) => setValue('icon', file)}
              />
            </Grid>
          </Grid>
          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={6}>
              <Controller
                name="deadlineType"
                control={control}
                render={({ field: { ref, onChange, ...field } }) => (
                  <FormSelectField
                    {...field}
                    inputRef={ref}
                    options={[
                      { label: 'None', value: EstateTaskDeadlineType.None },
                      {
                        label: 'Months from date of death',
                        value: EstateTaskDeadlineType.MonthsFromDateOfDeath,
                      },
                      { label: 'Tax day', value: EstateTaskDeadlineType.TaxDay },
                    ]}
                    label="Deadline"
                    errorMessage={errors.deadlineType?.message}
                    onChange={(evt) => {
                      const value = evt.target.value as EstateTaskDeadlineType;
                      onChange(value);
                      if (value === EstateTaskDeadlineType.MonthsFromDateOfDeath) {
                        setValue('deadlineMonthsFromDateOfDeath', 9);
                      } else {
                        setValue('deadlineMonthsFromDateOfDeath', 0);
                      }
                    }}
                  />
                )}
              />
            </Grid>
            {deadlineType === EstateTaskDeadlineType.MonthsFromDateOfDeath && (
              <Grid className={classes.inputGrid} item xs={12} sm={6}>
                <Controller
                  name="deadlineMonthsFromDateOfDeath"
                  control={control}
                  render={({ field: { ref, ...field } }) => (
                    <FormTextField
                      {...field}
                      inputRef={ref}
                      value={field.value}
                      errorMessage={errors.deadlineMonthsFromDateOfDeath?.message}
                      label="# of Months"
                      InputProps={{ inputProps: { min: 1 }, inputMode: 'numeric' }}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>

          <Grid container className={classes.container}>
            <Grid item xs={6} sm={6}>
              <Typography>Minimum Plan Level</Typography>
              <Controller
                name="minimumFeatureSubscriptionType"
                control={control}
                render={({ field: { ref, onChange, value, ...field } }) => (
                  <FormSelectField
                    inputRef={ref}
                    {...field}
                    placeholder="Minimum Plan Level"
                    options={getMinimumPlanOptions()}
                    value={value}
                    onChange={(evt) => {
                      onChange(evt.target.value);
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container className={classes.container}>
            <Grid item xs={4} sm={4}>
              <Typography variant="caption">Auto Document Generation</Typography>

              <Controller
                name="autoDocumentGeneration"
                control={control}
                render={({ field: { ref, onChange, value, ...field } }) => (
                  <Checkbox
                    {...field}
                    inputRef={ref}
                    onChange={(evt) => onChange(evt.target.checked)}
                    color="primary"
                    checked={value}
                    disabled={!hasAssignButton}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Divider light />

          <div style={{ marginTop: '10px' }}>
            {isEdit && (
              <div className={classes.buttonContainer}>
                <Button
                  color="primary"
                  variant="contained"
                  aria-label="Clear Document Generation Template Field"
                  onClick={() => {
                    setValue('documentGenerationTemplateId', '');
                  }}
                >
                  Clear Template Field
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  aria-label="Clear Conditional Template Questions"
                  onClick={() => {
                    setValue('conditionalTemplateQuestionSetId', '');
                  }}
                >
                  Clear Question Fields
                </Button>
              </div>
            )}
          </div>

          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="documentGenerationTemplateId"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <AutocompleteFormSelectField
                  {...field}
                  inputRef={ref}
                  value={field.value}
                  options={documentGenerationTemplateSelectOptions}
                  label="Document Generation Template"
                  handleOnChange={handleOnDocumentGenerationTemplateChange}
                />
              )}
            />
          </Grid>

          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="conditionalTemplateQuestionSetId"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormSelectField
                  {...field}
                  inputRef={ref}
                  defaultValue={field.value}
                  options={conditionalTemplateQuestionSetsSelectOptions}
                  label="Conditional Template Question Set"
                  onChange={(event) =>
                    handleOnconditionalTemplateQuestionSetChange(event?.target?.value as string)
                  }
                />
              )}
            />
          </Grid>

          {showAddNewEstateInputDatasButton && (
            <>
              <div className={classes.reqItemsTitle}>Required Info/Doc items</div>
              <Divider light />

              <div className={classes.container}>
                {createEstateInputDatasInputFields.map((field, index) => (
                  <RequiredInfoDocItem
                    key={field.id}
                    index={index}
                    isDisabled={isEdit}
                    errorMessage={
                      errors.createEstateInputDatasInput?.[index]?.estateInputDataFormatId?.message
                    }
                    onChange={(value) =>
                      updateCreateEstateInputDataInput(index, {
                        estateInputDataFormatId: value,
                      })
                    }
                    value={field.estateInputDataFormatId || ''}
                    onDelete={() => removeCreateEstateInputDataInput(index)}
                  />
                ))}
              </div>

              {isEdit && (
                <div className={classes.container}>
                  {extraEstateInputDatasFields.map((_field, index) => (
                    <RequiredInfoDocItem
                      key={index}
                      index={createEstateInputDatasInputFields.length + index}
                      errorMessage={
                        errors.extraEstateInputDatasInput?.[index]?.estateInputDataFormatId?.message
                      }
                      onChange={(value) =>
                        updateExtraEstateInputDataInput(index, {
                          estateInputDataFormatId: value,
                        })
                      }
                      onDelete={() => removeExtraEstateInputDataInput(index)}
                    />
                  ))}
                </div>
              )}

              <div className={classes.container}>
                <Link className={classes.addItem} onClick={onAddNewEstateInputDatasInput}>
                  <AddIcon /> <Typography>Add Required Info/Doc</Typography>
                </Link>
              </div>
            </>
          )}

          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={6}>
              <Button
                type="submit"
                form="estate-task-form"
                color="secondary"
                variant="contained"
                disabled={isSubmitting || isSaveDisabled}
              >
                Save
              </Button>
            </Grid>
            {isEdit && (
              <Grid className={classes.inputGrid} item xs={12} sm={6}>
                <Button
                  color="secondary"
                  variant="contained"
                  disabled={isSubmitting || isSaveDisabled}
                  onClick={handleUpdateIsArchived}
                >
                  {isArchived ? 'Unarchive' : 'Archive'}
                </Button>
              </Grid>
            )}
          </Grid>
        </div>
      </form>
    </FormProvider>
  );
};

export default EstateTaskForm;
